import Link from 'next/link';
import {
  type ComponentType,
  FC,
  type MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  memo,
  useCallback,
} from 'react';
import { isURL } from '@bridebook/toolbox/src/is';
import { A, Box } from '@bridebook/ui';
import { closeAllTooltips } from '@bridebook/ui/src/components/bbcommon/tooltip/tooltip';
import { styles } from '../popover-menu.style';

interface ItemProps extends PropsWithChildren {
  onClick?: MouseEventHandler<ReactNode>;
  link?: string;
  /**
   * Replaces the default Next.js link, for apps that need their own routing
   * behaviour such as prefixing the market.
   */
  linkComponent?: ComponentType<PropsWithChildren<{ href: string }>>;
  icon?: ReactNode;
  isSelected?: boolean;
  disabled?: boolean;
}

const PopoverMenuItemComp: FC<ItemProps> = ({
  children,
  icon,
  onClick,
  link,
  linkComponent: LinkComponent = Link,
  isSelected,
  disabled,
}) => {
  const _onClick: MouseEventHandler<ReactNode> = useCallback(
    (e) => {
      if (onClick) {
        onClick(e);
        closeAllTooltips();
      }
    },
    [onClick],
  );

  const isInteractive = !disabled && Boolean(onClick || link);

  const content = (
    <Box
      px={3}
      py={icon ? 2 : 3} // Icon wrapper is higher than one line of text, acting as a fake padding
      style={styles.itemWrap({
        isHighlighted: isSelected,
        isBold: isSelected,
        isInteractive,
        disabled,
      })}
      onClick={isInteractive ? _onClick : undefined}
      data-name="popover-menu-item">
      {icon && <Box style={styles.iconWrap}>{icon}</Box>}
      <Box style={styles.content}>{children}</Box>
    </Box>
  );

  return link && !disabled ? (
    isURL(link) ? (
      <A href={link} target="_blank" noUnderline color="space" w="100%">
        {content}
      </A>
    ) : (
      <LinkComponent href={link}>{content}</LinkComponent>
    )
  ) : (
    content
  );
};

/**
 * Base component for every item in the popover menu.
 * Provides default padding and hover effect, even if the content is more complex.
 *
 * For simple text menus, it provides a link or onClick handler, with an optional icon.
 */
export const PopoverMenuItem = memo(PopoverMenuItemComp);
